// src/pages/Services.jsx
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { SITE, SERVICES, servicesIndexUrl } from "../data/site";
import ServiceCard from "../components/ServiceCard";
import { setPageSeo } from "../utils/seo";

export default function Services() {
  const PAGE_URL = servicesIndexUrl();

  const SEO_TITLE =
    "Awnings, Roofing, Shelter, Zip Track & Metal Works in Singapore | Din Lee Engineering";
  const SEO_DESC =
    "Din Lee Engineering provides retractable awnings, rooftop awnings, loop awnings, shelters, glass canopy, ACP/polycarbonate/zinc roofing, outdoor zip track, roller blinds, metal works, decking and renovation support in Singapore. Site measurement, fabrication, installation and after-sales support. Call +65 8530 3990.";

  /**
   * ✅ CHANGE IMAGES HERE ONLY
   * Put your images in:
   * - public/images/...  (then use "/images/xxx.jpg")
   * OR
   * - src/assets/...     (then import and use imported variables)
   */
  const IMAGES = useMemo(
    () => ({
      hero: "/images/glass.jpg",

      // optional section images (you can change later)
      process: "/images/process.png",
      materials: "/images/Where-services.png",
      quote: "/images/Need-Fast-Quotation.png",

      // fallback image (if needed in future)
      fallback: "/images/placeholder.jpg",
    }),
    []
  );

  // ✅ Deep SEO content sections (paste-ready)
  const CONTENT = useMemo(
    () => ({
      trustBullets: [
        "Singapore-based workmanship with practical recommendations for heat, rain and wind exposure",
        "Accurate site measurement → fabrication → installation with clean finishing",
        "Material & structure guidance (ACP / polycarbonate / aluminium / zinc / glass canopy) based on your site",
        "After-sales support and clear handover checklist",
      ],
      compare: [
        {
          h: "Din Lee Engineering vs. typical contractor",
          rows: [
            [
              "Site measurement",
              "Accurate on-site measurement + recommendation",
              "Sometimes estimate-based",
            ],
            [
              "Material guidance",
              "Suggest best material for exposure & budget",
              "May push one material only",
            ],
            ["Finishing", "Sealing, edges, slope & drainage details", "Finishing can vary"],
            [
              "Stability & safety",
              "Proper mounting, frame support, load awareness",
              "May be minimal support",
            ],
            [
              "After-sales support",
              "Support for adjustments/maintenance advice",
              "Often limited",
            ],
          ],
        },
      ],
      processSteps: [
        {
          h: "1) Share your requirement",
          p: "Send your location, photos/video and the rough width/height. Tell us the service you want (e.g., retractable awning, polycarbonate roof, zip track).",
        },
        {
          h: "2) Site measurement",
          p: "We measure mounting points, slope/drainage direction, clearance and exposure (sun/rain/wind) to recommend a suitable structure and materials.",
        },
        {
          h: "3) Proposal & quotation",
          p: "We propose the recommended option (material/type/finish) and provide quotation based on size, complexity and installation conditions.",
        },
        {
          h: "4) Fabrication",
          p: "Fabrication with proper frame support and finishing details to improve durability and reduce leakage/noise risk.",
        },
        {
          h: "5) Installation & handover",
          p: "Installation with stable mounting, proper sealing and clean finishing. Handover includes basic usage and care/maintenance guidance.",
        },
      ],
      materials: [
        {
          h: "Materials we commonly work with",
          p: "For Singapore weather, we commonly recommend aluminium structures and weather-resistant systems such as ACP roofing, polycarbonate roof panels, zinc roofing, durable coated metal parts, and safety glass canopy systems—depending on your site exposure and use-case.",
        },
        {
          h: "Choosing the right roof / canopy type",
          p: "The best choice depends on sun direction, rain splash, wind corridor, and the area usage (car porch, patio, walkway, shopfront). We recommend slope/drainage planning to reduce leakage and water pooling.",
        },
      ],
      useCases: [
        {
          h: "Where our services are commonly used",
          items: [
            "HDB / Condo balconies, patios and household outdoor areas",
            "Landed homes: car porch roof, patio roof, side walkway shelter",
            "Commercial shopfront: loop awning, retractable awning and entrance canopy",
            "Industrial / warehouse: walkway shelter and functional roofing add-ons",
          ],
        },
      ],
      pricingFactors: [
        {
          h: "What affects the quotation",
          items: [
            "Size (width × projection/length) and height/clearance",
            "Material choice (ACP, polycarbonate, zinc, aluminium components, glass canopy)",
            "Frame design complexity and number of support posts/brackets",
            "Installation access (scaffold/lift requirement), site constraints and safety needs",
            "Add-ons (motorized system, side covers, zip track, sealing/drainage details)",
          ],
        },
        {
          h: "Fast quotation checklist",
          items: [
            "1) Location + photos (front + side angles)",
            "2) Approximate width/height",
            "3) Preferred service type (e.g., retractable awning / polycarbonate roof / zip track)",
            "4) Any special needs (privacy, heat reduction, rain protection, walkway coverage)",
          ],
        },
      ],
      areas: [
        {
          h: "Service coverage in Singapore",
          p: "We serve clients across Singapore. For faster scheduling, send your postcode and a few site photos. We will advise the next steps for measurement and quotation.",
        },
      ],
    }),
    []
  );

  const FAQ = useMemo(
    () => [
      {
        q: "What services do you provide in Singapore?",
        a: "We provide custom awnings (retractable, loop, rooftop), roofing and shelter solutions (ACP, polycarbonate, zinc, glass canopy), outdoor zip track and roller blinds, metal works & fabrication, wooden trellis/decking and renovation support depending on site needs.",
      },
      {
        q: "Do you offer site measurement and installation?",
        a: "Yes. We arrange site measurement, recommend suitable materials/design, fabricate and install with proper finishing and after-sales support.",
      },
      {
        q: "How fast can I get a quotation?",
        a: "For a faster quote, send your location (postcode), approximate size, photos and preferred awning/roof type. We will respond with a recommended solution and estimated cost direction.",
      },
      {
        q: "Which materials are suitable for Singapore weather?",
        a: "We recommend weather-resistant systems such as aluminium structures, ACP roofing, polycarbonate panels, zinc roofing and safety glass canopy depending on exposure and site conditions.",
      },
      {
        q: "How do I contact you?",
        a: `You can call or WhatsApp us at ${SITE.phone} or email ${SITE.emailPrimary}. Our working hours are Mon–Sat (9am–6pm).`,
      },
    ],
    []
  );

  // ✅ JSON-LD schemas
  const schemas = useMemo(() => {
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Din Lee Engineering Services",
      itemListElement: SERVICES.map((s, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: s.title,
        url: `${SITE.domain}/services/${s.slug}`,
      })),
    };

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE.domain },
        { "@type": "ListItem", position: 2, name: "Services", item: PAGE_URL },
      ],
    };

    const webPage = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Services",
      url: PAGE_URL,
      description: SEO_DESC,
      isPartOf: { "@type": "WebSite", name: SITE.brand, url: SITE.domain },
    };

    const localBusiness = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: SITE.brand,
      url: SITE.domain,
      telephone: SITE.phone,
      email: SITE.emailPrimary,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE.addressLine,
        addressLocality: "Singapore",
        postalCode: "757700",
        addressCountry: "SG",
      },
      areaServed: "Singapore",
      geo: {
        "@type": "GeoCoordinates",
        latitude: SITE.geo.lat,
        longitude: SITE.geo.lng,
      },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          opens: "09:00",
          closes: "18:00",
        },
      ],
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((x) => ({
        "@type": "Question",
        name: x.q,
        acceptedAnswer: { "@type": "Answer", text: x.a },
      })),
    };

    return [itemList, breadcrumb, webPage, localBusiness, faqSchema];
  }, [FAQ, PAGE_URL]);

  useEffect(() => {
    setPageSeo({
      title: SEO_TITLE,
      description: SEO_DESC,
      canonical: PAGE_URL,
    });
  }, [PAGE_URL]);

  return (
    <>
      {/* ✅ JSON-LD: multiple schemas */}
      {schemas.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}

      {/* ================= TOP BANNER ================= */}
      <section className="bg-gradient-to-b from-slate-50 to-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">
              Awnings, Roofing, Shelter & Outdoor Systems in Singapore
            </h1>

            <p className="mt-4 text-slate-600 leading-relaxed">
              <span className="font-semibold text-slate-900">{SITE.brand}</span>{" "}
              provides practical, site-based solutions for{" "}
              <span className="font-semibold text-slate-900">
                retractable awning Singapore
              </span>
              ,{" "}
              <span className="font-semibold text-slate-900">
                polycarbonate roof Singapore
              </span>
              , <span className="font-semibold text-slate-900">ACP roofing</span>{" "}
              and{" "}
              <span className="font-semibold text-slate-900">
                outdoor zip track Singapore
              </span>
              . We handle site measurement, fabrication and installation with clean
              finishing and after-sales support.
            </p>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-base font-extrabold text-slate-900">
                Why clients choose us
              </h2>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                {CONTENT.trustBullets.map((t) => (
                  <li key={t} className="flex gap-2">
                    <span className="mt-[2px] text-emerald-600">✓</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 flex gap-3 flex-wrap">
              <Link
                to="/contact"
                className="px-5 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
              >
                Request a Quote
              </Link>
              <a
                href={`tel:${SITE.phone}`}
                className="px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition"
              >
                Call Now
              </a>
            </div>

            <div className="mt-4 text-sm text-slate-600">
              <span className="font-semibold text-slate-900">Fast Quote:</span>{" "}
              Send postcode + photos + rough size on WhatsApp.
            </div>
          </div>

          <div className="rounded-3xl overflow-hidden border bg-white shadow-sm">
            <img
              src={IMAGES.hero}
              alt="Din Lee Engineering awnings roofing shelter and zip track in Singapore"
              className="h-64 sm:h-80 w-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = IMAGES.fallback;
              }}
            />
            <div className="p-5 border-t">
              <p className="text-sm text-slate-600 leading-relaxed">
                We focus on durability and finishing details—especially sealing, slope/drainage
                planning and stable mounting—so your outdoor structure performs well in Singapore
                weather.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICES GRID ================= */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-extrabold text-slate-900">All Service Categories</h2>
            <p className="text-slate-600 mt-2">
              Click any service to view details, FAQs and quotation tips.
            </p>
          </div>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s) => (
            <div key={s.slug}>
              <ServiceCard
                title={s.title}
                desc={s.desc}
                href={`/services/${s.slug}`}
                image={s.cardImage || s.heroImage || "/images/glass.jpg"}
                alt={`${s.title} service in Singapore`}
              />
            </div>
          ))}
        </div>
      </section>

      {/* ================= DEEP CONTENT: PROCESS ================= */}
      <section className="bg-slate-50 border-t border-b">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="max-w-3xl">
            <h2 className="text-2xl font-extrabold text-slate-900">
              Our Work Process (Measurement → Fabrication → Installation)
            </h2>
            <p className="mt-2 text-slate-600 leading-relaxed">
              Outdoor awnings and roofs in Singapore must handle sun, heavy rain and wind. Our
              workflow focuses on accurate measurement, stable support structure and clean
              finishing—so your awning/roof/shelter lasts longer with fewer issues.
            </p>
          </div>

          <div className="mt-6 rounded-2xl overflow-hidden border bg-white">
            <img
              src={IMAGES.process}
              alt="Site measurement, fabrication and installation process"
              className="h-56 sm:h-72 w-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = IMAGES.fallback;
              }}
            />
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {CONTENT.processSteps.map((x) => (
              <div key={x.h} className="rounded-2xl border border-slate-200 bg-white p-6">
                <h3 className="text-lg font-extrabold text-slate-900">{x.h}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{x.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= DEEP CONTENT: MATERIALS + USE CASES ================= */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl overflow-hidden border bg-white">
            <img
              src={IMAGES.materials}
              alt="Materials for awnings roofing shelter and canopy systems"
              className="h-56 sm:h-72 w-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = IMAGES.fallback;
              }}
            />
          </div>

          {CONTENT.materials.map((m) => (
            <div key={m.h} className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-xl font-extrabold text-slate-900">{m.h}</h2>
              <p className="mt-2 text-slate-600 leading-relaxed">{m.p}</p>
            </div>
          ))}

          {CONTENT.pricingFactors.map((b) => (
            <div key={b.h} className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-xl font-extrabold text-slate-900">{b.h}</h2>
              <ul className="mt-3 space-y-2 text-slate-600">
                {b.items.map((it) => (
                  <li key={it} className="flex gap-2">
                    <span className="mt-[2px] text-blue-600">•</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <aside className="lg:col-span-1 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="text-lg font-extrabold text-slate-900">Where clients use our services</h3>
            <ul className="mt-3 space-y-2 text-slate-600">
              {CONTENT.useCases[0].items.map((it) => (
                <li key={it} className="flex gap-2">
                  <span className="mt-[2px] text-emerald-600">✓</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>

          {CONTENT.areas.map((a) => (
            <div key={a.h} className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-extrabold text-slate-900">{a.h}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{a.p}</p>

              <div className="mt-4 flex gap-3 flex-wrap">
                <a
                  href={`tel:${SITE.phone}`}
                  className="inline-block px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition"
                >
                  Call {SITE.phone}
                </a>
                <Link
                  to="/contact"
                  className="inline-block px-5 py-3 rounded-xl border border-slate-300 text-slate-900 font-semibold hover:bg-slate-50 transition"
                >
                  Contact Form
                </Link>
              </div>
            </div>
          ))}
        </aside>
      </section>

      {/* ================= COMPARISON (TRUST) ================= */}
      <section className="bg-slate-50 border-t border-b">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="max-w-3xl">
            <h2 className="text-2xl font-extrabold text-slate-900">
              Workmanship & Support (Trust Section)
            </h2>
            <p className="mt-2 text-slate-600 leading-relaxed">
              Many issues (leakage, noise, shaky frames) are caused by weak support, poor slope
              planning or rushed sealing. We focus on stable mounting and finishing details so the
              final result looks neat and performs better.
            </p>
          </div>

          {CONTENT.compare.map((c) => (
            <div key={c.h} className="mt-8 overflow-hidden rounded-2xl border bg-white">
              <div className="border-b px-5 py-4">
                <h3 className="text-lg font-extrabold text-slate-900">{c.h}</h3>
              </div>

              <div className="grid md:grid-cols-3 gap-0">
                <div className="p-4 text-sm font-bold text-slate-900 bg-slate-50 border-b md:border-b-0 md:border-r">
                  Item
                </div>
                <div className="p-4 text-sm font-bold text-slate-900 bg-slate-50 border-b md:border-b-0 md:border-r">
                  {SITE.brand}
                </div>
                <div className="p-4 text-sm font-bold text-slate-900 bg-slate-50">
                  Typical
                </div>

                {c.rows.map((r, i) => (
                  <div key={i} className="contents">
                    <div className="p-4 text-sm text-slate-700 border-t md:border-r">{r[0]}</div>
                    <div className="p-4 text-sm text-slate-700 border-t md:border-r">{r[1]}</div>
                    <div className="p-4 text-sm text-slate-700 border-t">{r[2]}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="max-w-3xl">
          <h2 className="text-2xl font-extrabold text-slate-900">Frequently Asked Questions</h2>
          <p className="text-slate-600 mt-2">
            Quick answers about awnings, roofing, shelter, zip track and metal works services in
            Singapore.
          </p>
        </div>

        <div className="mt-8 grid gap-3 max-w-3xl">
          {FAQ.map((x) => (
            <details key={x.q} className="group rounded-2xl border border-slate-200 bg-white p-5">
              <summary className="cursor-pointer list-none font-bold text-slate-900 flex items-center justify-between">
                <span>{x.q}</span>
                <span className="text-slate-400 group-open:rotate-180 transition">▾</span>
              </summary>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{x.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 py-12 text-white grid md:grid-cols-2 gap-6 items-center">
          <div>
            <h3 className="text-2xl font-extrabold">Need Fast Quotation?</h3>
            <p className="text-white/80 mt-2">
              Send postcode + photos + approximate size, and tell us your preferred awning / roof
              type. We will propose a suitable option.
            </p>
          </div>

          <div className="md:justify-self-end w-full md:w-[360px] rounded-2xl overflow-hidden border border-white/15 bg-white/5">
            <img
              src={IMAGES.quote}
              alt="Quotation support for awnings roofing shelter and zip track in Singapore"
              className="h-40 w-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = IMAGES.fallback;
              }}
            />
          </div>

          <div className="md:text-right flex md:justify-end gap-3 flex-wrap md:col-span-2">
            <a
              href={`tel:${SITE.phone}`}
              className="inline-block px-6 py-3 rounded-xl bg-white text-slate-900 font-bold hover:bg-slate-100 transition"
            >
              Call {SITE.phone}
            </a>

            <Link
              to="/contact"
              className="inline-block px-6 py-3 rounded-xl border border-white/30 text-white font-bold hover:bg-white/10 transition"
            >
              Contact Form
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
